import { useRef, useState, type ChangeEvent } from "react";
import { Modal } from "@/components/modal";
import { UploadCard, downloadBatchTemplate } from "@/app/(app)/batch/_components/batch-empty-state";
import { uploadBatch } from "@/src/services/monitoring_service";
import { captureEvent } from "@/src/lib/posthog";

type Props = {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
};

export function UploadBatchModal({ open, onClose, onUploaded }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function processFile(file: File) {
    setError(null);
    setUploading(true);
    const res = await uploadBatch(file);
    setUploading(false);
    if (res.ok) {
      captureEvent("Upload batch");
      onUploaded();
    } else {
      setError(res.error.message || "Couldn't upload the file. Please try again.");
    }
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void processFile(file);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload monitoring file"
      description="Add a list of entities to continuously monitor."
      iconName="upload_file"
      width={760}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        className="hidden"
        onChange={handleFile}
      />
      <UploadCard
        onUpload={() => fileInputRef.current?.click()}
        onFileDrop={(file) => void processFile(file)}
        onDownloadTemplate={downloadBatchTemplate}
        infoFontSize="12px"
        heading="Upload a new Monitoring File"
        description={
          <p>
            Please use our XLS template to upload your entities and check our{" "}
            <span className="font-medium text-black">Continuous Monitoring Guide</span>.
          </p>
        }
      />
      {uploading && <p className="text-small text-grey-900">Uploading…</p>}
      {error && (
        <p role="alert" className="text-small text-red-600">
          {error}
        </p>
      )}
    </Modal>
  );
}
